import { Badge, Box, Button, Group, Modal, Paper, Stack, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconDownload, IconPlayerPlay, IconSparkles, IconTrash } from "@tabler/icons-react";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import Loading from "../Loading";
import { processingStatusLabel, useProcessing } from "../utils";
import {
    type TtsProjectDeleteMutation,
    type TtsProjectDeleteMutationVariables,
    type TtsProjectDownloadMutation,
    type TtsProjectDownloadMutationVariables,
    type TtsProjectGenerateAllMutation,
    type TtsProjectGenerateAllMutationVariables,
    type TtsProjectQuery,
    type TtsProjectQueryVariables,
    type TtsBlockMoveMutation,
    type TtsBlockMoveMutationVariables,
    ttsBlockMoveMutation,
    ttsProjectDeleteMutation,
    ttsProjectDownloadMutation,
    ttsProjectGenerateAllMutation,
    ttsProjectQuery,
} from "./queries";
import BlockList from "./BlockList";
import TtsBlockCard from "./TtsBlockCard";
import TtsBlockForm from "./TtsBlockForm";
import { useBlockPreview } from "./useBlockPreview";
import classes from "./TtsProjectView.module.css";

function TtsProjectView(): JSX.Element {
    const params = useParams();
    const navigate = useNavigate();
    const id = useMemo(() => Number(params.id), [params]);

    const [result, reexecute] = useQuery<TtsProjectQuery, TtsProjectQueryVariables>({
        query: ttsProjectQuery,
        variables: { id },
    });

    useProcessing(result, reexecute, (r) => r.data?.ttsProject?.processingStatus === "PROCESSING");

    const [, executeDelete] = useMutation<TtsProjectDeleteMutation, TtsProjectDeleteMutationVariables>(
        ttsProjectDeleteMutation,
    );
    const [generateResult, executeGenerate] = useMutation<
        TtsProjectGenerateAllMutation,
        TtsProjectGenerateAllMutationVariables
    >(ttsProjectGenerateAllMutation);
    const [downloadResult, executeDownload] = useMutation<
        TtsProjectDownloadMutation,
        TtsProjectDownloadMutationVariables
    >(ttsProjectDownloadMutation);
    const [moveResult, executeMove] = useMutation<TtsBlockMoveMutation, TtsBlockMoveMutationVariables>(
        ttsBlockMoveMutation,
    );
    const [isAddBlockOpen, { open: openAddBlock, close: closeAddBlock }] = useDisclosure(false);
    const [selectedBlockId, setSelectedBlockId] = useState<number | null>(null);

    const onDelete = async () => {
        const res = await executeDelete({ id });
        if (res.error) {
            notifications.show({ title: "Failed to delete", message: res.error.message });
        } else {
            notifications.show({ title: "Project deleted", message: `Project deleted` });
            navigate("/tts");
        }
    };

    const confirmDelete = () =>
        modals.openConfirmModal({
            title: "Delete project?",
            children: <Text size="sm">Are you sure you want to delete this TTS project and all its blocks?</Text>,
            labels: { confirm: "Delete", cancel: "Cancel" },
            confirmProps: { color: "red" },
            onConfirm: onDelete,
        });

    const onGenerateAll = async () => {
        const res = await executeGenerate({ id });
        if (res.error) {
            notifications.show({ title: "Failed to generate", message: res.error.message });
        } else {
            notifications.show({ title: "Generation started", message: "Audio generation started in background" });
            reexecute({ requestPolicy: "network-only" });
        }
    };

    const onDownload = async () => {
        const res = await executeDownload({ id });
        if (res.error) {
            notifications.show({ title: "Failed to download", message: res.error.message });
        } else if (res.data?.ttsProjectDownload) {
            window.open(res.data.ttsProjectDownload, "_blank");
        }
    };

    const onBlockCreated = () => {
        reexecute({ requestPolicy: "network-only" });
        closeAddBlock();
    };

    const project = result.data?.ttsProject ?? null;
    const blocks = project?.blocks.edges.map((e) => e.node) ?? [];
    const hasStaleBlocks = blocks.some((b) => b.audioStale || !b.file);
    const isProcessing = project?.processingStatus === "PROCESSING";
    const hasPreviewableAudio = blocks.length > 0 && blocks.every((b) => b.file?.src && !b.audioStale);
    const { isPreviewPlaying, playWithSilence, stopPreview } = useBlockPreview(blocks);

    useEffect(() => {
        if (blocks.length === 0) {
            setSelectedBlockId(null);
            return;
        }
        if (selectedBlockId === null || !blocks.find((b) => b.id === selectedBlockId)) {
            setSelectedBlockId(blocks[0]?.id ?? null);
        }
    }, [blocks, selectedBlockId]);

    const handlePreviewClick = useCallback(async () => {
        if (isPreviewPlaying) {
            stopPreview();
            return;
        }
        await playWithSilence(hasPreviewableAudio);
    }, [hasPreviewableAudio, isPreviewPlaying, playWithSilence, stopPreview]);

    if (!Number.isFinite(id)) {
        navigate("/tts");
        return <Loading />;
    }

    if (result.fetching && !result.data) {
        return <Loading />;
    }
    if (result.error && !result.data) {
        return <Box>Error: {result.error.message}</Box>;
    }
    if (!project) {
        return <Box>Project not found</Box>;
    }

    const selectedBlock = blocks.find((b) => b.id === selectedBlockId) ?? null;

    const handleMove = async (blockId: number, direction: -1 | 1) => {
        const block = blocks.find((b) => b.id === blockId);
        if (!block) {
            return;
        }
        const newPosition = block.position + direction;
        if (newPosition < 0 || newPosition >= blocks.length) {
            return;
        }
        const res = await executeMove({ id: blockId, newPosition });
        if (res.error) {
            notifications.show({ title: "Failed to reorder block", message: res.error.message });
            return;
        }
        setSelectedBlockId(blockId);
        reexecute({ requestPolicy: "network-only" });
    };

    return (
        <>
            <Modal opened={isAddBlockOpen} onClose={closeAddBlock} title="Add Block" centered size="lg">
                <TtsBlockForm projectId={id} onCreated={onBlockCreated} />
            </Modal>
            <Stack maw={1100} mx="auto" gap="lg" className={classes.layout}>
                <Group justify="space-between" align="center">
                    <Title order={2} fw={700}>
                        {project.name}
                    </Title>
                    <Group gap="sm">
                        {result.fetching ? (
                            <Badge color="gray" variant="light">
                                Updating…
                            </Badge>
                        ) : null}
                        <Badge color={project.processingStatus === "PROCESSING" ? "yellow" : "green"} variant="light">
                            {processingStatusLabel(project.processingStatus)}
                        </Badge>
                        <Button
                            color="red"
                            variant="subtle"
                            onClick={confirmDelete}
                            leftSection={<IconTrash size={16} />}
                            size="sm"
                        >
                            Delete Project
                        </Button>
                    </Group>
                </Group>

                <Group gap="sm">
                    <Button
                        leftSection={<IconSparkles size={16} />}
                        onClick={onGenerateAll}
                        disabled={!hasStaleBlocks || isProcessing}
                        loading={generateResult.fetching}
                    >
                        Generate All Audio
                    </Button>
                    <Button
                        leftSection={<IconPlayerPlay size={16} />}
                        onClick={handlePreviewClick}
                        disabled={!hasPreviewableAudio || isProcessing}
                    >
                        {isPreviewPlaying ? "Stop Preview" : "Preview Audio"}
                    </Button>
                    <Button
                        leftSection={<IconDownload size={16} />}
                        onClick={onDownload}
                        disabled={blocks.length === 0 || isProcessing}
                        loading={downloadResult.fetching}
                    >
                        Download Audio
                    </Button>
                </Group>

                <Group align="flex-start" gap="xl" wrap="wrap">
                    <BlockList
                        blocks={blocks}
                        selectedBlockId={selectedBlockId}
                        onSelect={(blockId) => setSelectedBlockId(blockId)}
                        onMove={handleMove}
                        onAdd={openAddBlock}
                        isProcessing={Boolean(isProcessing)}
                        isMoveFetching={moveResult.fetching}
                    />

                    <Paper withBorder shadow="xs" p="md" radius="md" className={classes.editorPanel}>
                        <Text fw={700} mb="sm">
                            Block Editor
                        </Text>
                        {selectedBlock ? (
                            <TtsBlockCard
                                key={selectedBlock.id}
                                block={selectedBlock}
                                isProcessing={isProcessing}
                                onUpdate={() => reexecute({ requestPolicy: "network-only" })}
                            />
                        ) : (
                            <Paper withBorder p="lg" radius="md" className={classes.emptyState}>
                                <Text c="dimmed">Select a block to edit.</Text>
                            </Paper>
                        )}
                    </Paper>
                </Group>

                <Link to="/tts">Back</Link>
            </Stack>
        </>
    );
}

export default TtsProjectView;
